import {makeAutoObservable, runInAction} from 'mobx';
import {networkOptimization} from '../../../../lib/api';
import {UploadJob, UploadJobPhase, UPLOAD_JOB_PHASE} from '../types';
import {safeConsoleError, safeConsoleLog} from '../../../../lib/sanitize';

/**
 * Manages file upload job lifecycle including chunked uploads, progress tracking,
 * retry logic, and persistence across browser sessions.
 */
export class FileUploadStore {
    uploadJobs: UploadJob[] = [];
    private _isRunning = false;
    private _uploadAbortControllers = new Map<string, AbortController>();
    private static readonly UPLOAD_PARALLEL_CHUNKS = 4;
    private static readonly UPLOAD_MAX_CHUNK_RETRIES = 3;
    private static readonly UPLOAD_JOBS_KEY = 'network_optimization_upload_jobs';
    private static readonly PROGRESS_CHUNK_MAX = 85;
    private static readonly PROGRESS_ASSEMBLY_START = 86;
    private static readonly PROGRESS_COMPLETE = 100;

    constructor(
        private filesService: any,
        private showToastNotification: (kind: 'success' | 'error' | 'info' | 'warning', title: string, subtitle: string, options?: {errorId?: string; action?: {label: string; onClick: () => void}; timeout?: number}) => void,
        private networkOptimizationApi: any,
        private uploadChunkSize: number,
        private onJobStatusChange: (fileId: string, status: string) => void
    ) {
        makeAutoObservable(this, {
            _isRunning: false,
        } as any);
    }

    get hasActiveUploads(): boolean {
        return this.uploadJobs.some(
            (job) =>
                job.phase === 'initializing' ||
                job.phase === 'uploading' ||
                job.phase === 'assembling'
        );
    }

    get activeUploadCount(): number {
        return this.uploadJobs.filter(
            (job) =>
                job.phase === 'initializing' ||
                job.phase === 'uploading' ||
                job.phase === 'assembling'
        ).length;
    }

    startFileUploads(files: File[]): void {
        const updatedJobs = [...this.uploadJobs];
        const newJobs: UploadJob[] = [];

        for (const file of files) {
            const failedIdx = updatedJobs.findIndex(
                j => j.filename === file.name &&
                (j.phase === UPLOAD_JOB_PHASE.ERROR || j.phase === UPLOAD_JOB_PHASE.INTERRUPTED)
            );

            if (failedIdx !== -1) {
                updatedJobs[failedIdx] = {
                    ...updatedJobs[failedIdx],
                    file,
                    phase: UPLOAD_JOB_PHASE.UPLOADING,
                    progress: 0,
                    completedChunks: 0,
                    totalChunks: Math.ceil(file.size / this.uploadChunkSize),
                };
            } else {
                newJobs.push({
                    id: crypto.randomUUID(),
                    filename: file.name,
                    fileSize: file.size,
                    phase: UPLOAD_JOB_PHASE.UPLOADING,
                    progress: 0,
                    totalChunks: Math.ceil(file.size / this.uploadChunkSize),
                    completedChunks: 0,
                    file,
                });
            }
        }

        runInAction(() => {
            this.uploadJobs.splice(0, this.uploadJobs.length, ...updatedJobs, ...newJobs);
        });
        this._persistUploadJobs();
        if (!this._isRunning) {
            this._startRunner();
        }
    }

    clearCompletedJobs(): void {
        runInAction(() => {
            const filtered = this.uploadJobs.filter(j => j.phase !== 'done');
            this.uploadJobs.splice(0, this.uploadJobs.length, ...filtered);
        });
        this._persistUploadJobs();
    }

    removeJob(id: string): void {
        const job = this.uploadJobs.find(j => j.id === id);
        runInAction(() => {
            const filtered = this.uploadJobs.filter(j => j.id !== id);
            this.uploadJobs.splice(0, this.uploadJobs.length, ...filtered);
        });
        if (job?.jobType === 'resubmit' && job.fileId &&
            (job.phase === 'error' || job.phase === 'interrupted')) {
            this.onJobStatusChange(job.fileId, 'needs-review');
        }
        this._persistUploadJobs();
    }

    retryJob(id: string): void {
        const job = this.uploadJobs.find(j => j.id === id);
        if (!job?.file) return;
        runInAction(() => {
            const updated = this.uploadJobs.map(j =>
                j.id === id
                    ? {
                        ...j,
                        phase: 'uploading' as UploadJobPhase,
                        progress: 0,
                        completedChunks: 0,
                        uploadId: undefined,
                        errorMessage: undefined,
                    }
                    : j
            );
            this.uploadJobs.splice(0, this.uploadJobs.length, ...updated);
        });
        if (job.jobType === 'resubmit' && job.fileId) {
            this.onJobStatusChange(job.fileId, 'uploading');
        }
        this._persistUploadJobs();
        if (!this._isRunning) {
            this._startRunner();
        }
    }

    cancelJob(id: string): void {
        const job = this.uploadJobs.find(j => j.id === id);
        runInAction(() => {
            const filtered = this.uploadJobs.filter(j => j.id !== id);
            this.uploadJobs.splice(0, this.uploadJobs.length, ...filtered);
        });
        if (job?.jobType === 'resubmit' && job.fileId) {
            this.onJobStatusChange(job.fileId, 'needs-review');
        }
        this._persistUploadJobs();
        const ctrl = this._uploadAbortControllers.get(id);
        if (ctrl) {
            ctrl.abort();
            this._uploadAbortControllers.delete(id);
        }
    }

    cancelAllJobs(): void {
        const activeJobs = this.uploadJobs.filter(
            j => j.phase === 'initializing' || j.phase === 'uploading' || j.phase === 'assembling'
        );
        const activeIds = new Set(activeJobs.map(j => j.id));
        activeJobs.forEach(j => {
            if (j.jobType === 'resubmit' && j.fileId) {
                this.onJobStatusChange(j.fileId, 'needs-review');
            }
        });
        runInAction(() => {
            const filtered = this.uploadJobs.filter(j => !activeIds.has(j.id));
            this.uploadJobs.splice(0, this.uploadJobs.length, ...filtered);
        });
        this._persistUploadJobs();
        activeIds.forEach(id => {
            const ctrl = this._uploadAbortControllers.get(id);
            if (ctrl) {
                ctrl.abort();
                this._uploadAbortControllers.delete(id);
            }
        });
    }

    clearUploadJobs(): void {
        this._uploadAbortControllers.forEach(ctrl => ctrl.abort());
        this._uploadAbortControllers.clear();
        this._isRunning = false;
        runInAction(() => {
            this.uploadJobs.splice(0, this.uploadJobs.length);
        });
        sessionStorage.removeItem(FileUploadStore.UPLOAD_JOBS_KEY);
    }

    restoreUploadJobs(): void {
        try {
            const stored = sessionStorage.getItem(FileUploadStore.UPLOAD_JOBS_KEY);
            if (!stored) return;
            const persisted: Omit<UploadJob, 'file'>[] = JSON.parse(stored);

            const restored: UploadJob[] = persisted.map(j => {
                if (j.phase === 'assembling') return j;
                if (j.phase === 'initializing' || j.phase === 'uploading') {
                    return {...j, phase: 'interrupted' as UploadJobPhase, errorMessage: 'Upload failed. Please try again.'};
                }
                return j;
            });

            runInAction(() => {
                this.uploadJobs.splice(0, this.uploadJobs.length, ...restored);
            });

            restored
                .filter(j => j.phase === 'assembling' && j.uploadId)
                .forEach(j => {
                    const abort = new AbortController();
                    this._uploadAbortControllers.set(j.id, abort);
                    this._pollUploadStatus(j.uploadId!, j.id, abort.signal)
                        .then(() => {
                            runInAction(() => this._updateJob(j.id, {phase: 'done', progress: FileUploadStore.PROGRESS_COMPLETE}));
                            this._persistUploadJobs();
                        })
                        .catch((err: unknown) => {
                            if (!(err instanceof Error && err.name === 'AbortError')) {
                                runInAction(() => this._updateJob(j.id, {phase: 'error', errorMessage: 'Assembly failed on the server.'}));
                                if (j.jobType === 'resubmit' && j.fileId) {
                                    this.onJobStatusChange(j.fileId, 'needs-review');
                                }
                                this._persistUploadJobs();
                            }
                        })
                        .finally(() => this._uploadAbortControllers.delete(j.id));
                });
        } catch (err) {
            safeConsoleError('Failed to restore upload jobs:', err);
        }
    }

    startResubmit(fileId: string, file: File, uploadChunkSize: number): void {
        const job: UploadJob = {
            id: crypto.randomUUID(),
            filename: file.name,
            fileSize: file.size,
            phase: 'uploading' as UploadJobPhase,
            progress: 0,
            totalChunks: Math.ceil(file.size / uploadChunkSize),
            completedChunks: 0,
            file,
            jobType: 'resubmit',
            fileId,
        };
        runInAction(() => {
            this.uploadJobs.push(job);
        });
        this.onJobStatusChange(fileId, 'uploading');
        this._persistUploadJobs();
        this._startRunner();
    }

    private async _startRunner(): Promise<void> {
        this._isRunning = true;
        try {
            while (true) {
                const nextJob = this.uploadJobs.find(j =>
                    j.file && (j.phase === 'uploading' || j.phase === 'initializing')
                );
                if (!nextJob) {
                    this._isRunning = false;
                    return;
                }

                const abort = new AbortController();
                this._uploadAbortControllers.set(nextJob.id, abort);
                try {
                    await this._uploadFile(nextJob, abort.signal);
                } catch (err) {
                    if (err instanceof Error && err.name === 'AbortError') {
                        safeConsoleLog(`[Upload] Job ${nextJob.id} aborted`);
                    } else {
                        safeConsoleError(`[Upload] Job ${nextJob.id} failed:`, err);
                    }
                } finally {
                    this._uploadAbortControllers.delete(nextJob.id);
                }
            }
        } finally {
            this._isRunning = false;
        }
    }

    private async _uploadFile(job: UploadJob, signal: AbortSignal): Promise<void> {
        const file = job.file!;
        const jobType = job.jobType || 'new';
        const fileId = job.fileId;

        try {
            const initiateResp = await this.filesService.initiateFileUpload(
                file.name,
                file.size,
                jobType,
                fileId,
                signal
            );
            const uploadId = initiateResp.upload_id;

            runInAction(() => this._updateJob(job.id, {uploadId, progress: 2}));
            this._persistUploadJobs();

            const chunkCount = Math.ceil(file.size / this.uploadChunkSize);
            const chunkParts: {partNumber: number; etag: string}[] = [];
            const chunksInFlight = new Set<number>();
            const chunkPromises: Promise<void>[] = [];
            let nextChunkIdx = 0;
            let completedChunks = 0;
            let chunkError: Error | null = null;

            const uploadChunk = async (chunkIdx: number, retries = 0): Promise<void> => {
                if (signal.aborted) throw new Error('AbortError');
                chunksInFlight.add(chunkIdx);
                const start = chunkIdx * this.uploadChunkSize;
                const end = Math.min(start + this.uploadChunkSize, file.size);
                const chunk = file.slice(start, end);
                const partNumber = chunkIdx + 1;

                try {
                    const resp = await this.filesService.uploadFileChunk(uploadId, partNumber, chunk, signal);
                    chunkParts.push({partNumber, etag: resp.etag});
                    completedChunks++;
                    const chunkProgress = Math.min(
                        FileUploadStore.PROGRESS_CHUNK_MAX,
                        Math.floor((completedChunks / chunkCount) * FileUploadStore.PROGRESS_CHUNK_MAX)
                    );
                    runInAction(() => this._updateJob(job.id, {completedChunks, progress: chunkProgress}));
                    this._persistUploadJobs();
                } catch (err: any) {
                    if (err.name === 'AbortError') throw err;
                    if (retries < FileUploadStore.UPLOAD_MAX_CHUNK_RETRIES) {
                        await new Promise(res => setTimeout(res, 1000 * (retries + 1)));
                        return uploadChunk(chunkIdx, retries + 1);
                    }
                    const error = new Error(`Chunk ${partNumber} failed after ${retries} retries`);
                    chunkError = error;
                    throw error;
                } finally {
                    chunksInFlight.delete(chunkIdx);
                }
            };

            while (nextChunkIdx < chunkCount || chunksInFlight.size > 0) {
                while (chunksInFlight.size < FileUploadStore.UPLOAD_PARALLEL_CHUNKS && nextChunkIdx < chunkCount) {
                    const promise = uploadChunk(nextChunkIdx++).catch(err => {
                        // Errors are tracked in chunkError, prevent unhandled promise rejection
                        if (err.name !== 'AbortError') {
                            return;
                        }
                        throw err;
                    });
                    chunkPromises.push(promise);
                }
                await new Promise(res => setTimeout(res, 100));

                // If we have a chunk error, break out early
                if (chunkError) {
                    break;
                }
            }

            // Wait for all chunks to complete
            await Promise.allSettled(chunkPromises);

            // If any chunk failed, throw the error
            if (chunkError) {
                throw chunkError;
            }

            runInAction(() => this._updateJob(job.id, {phase: 'assembling', progress: FileUploadStore.PROGRESS_ASSEMBLY_START}));
            this._persistUploadJobs();

            chunkParts.sort((a, b) => a.partNumber - b.partNumber);
            await this.filesService.completeFileUpload(uploadId, chunkParts, signal);

            await this._pollUploadStatus(uploadId, job.id, signal);

            runInAction(() => this._updateJob(job.id, {phase: 'done', progress: FileUploadStore.PROGRESS_COMPLETE}));
            this._persistUploadJobs();
            this.showToastNotification(
                'success',
                'Upload Complete',
                `${file.name} uploaded successfully and is being processed`
            );
        } catch (err: any) {
            if (err.name === 'AbortError') throw err;
            runInAction(() => this._updateJob(job.id, {phase: 'error', errorMessage: err.message || 'Upload failed'}));
            if (job.jobType === 'resubmit' && job.fileId) {
                this.onJobStatusChange(job.fileId, 'needs-review');
            }
            this._persistUploadJobs();
            this.showToastNotification('error', 'Upload Failed', `${file.name}: ${err.message || 'Unknown error'}`);
            throw err;
        }
    }

    private async _pollUploadStatus(uploadId: string, jobId: string, signal: AbortSignal): Promise<void> {
        let attempts = 0;
        const maxAttempts = 120;
        while (attempts < maxAttempts) {
            if (signal.aborted) throw new Error('AbortError');
            await new Promise(res => setTimeout(res, 2000));
            try {
                const status = await this.filesService.getUploadStatus(uploadId, signal);
                if (status.processing_status === 'completed' || status.processing_status === 'queued') {
                    return;
                }
                if (status.processing_status === 'failed') {
                    throw new Error('Assembly failed on server');
                }
            } catch (err: any) {
                if (err.name === 'AbortError') throw err;
                // Re-throw assembly failure errors so they can be caught by the outer handler
                if (err.message && err.message.includes('Assembly failed')) throw err;
                safeConsoleError('Poll status error:', err);
            }
            attempts++;
        }
        throw new Error('Assembly timeout');
    }

    private _updateJob(id: string, updates: Partial<UploadJob>): void {
        const idx = this.uploadJobs.findIndex(j => j.id === id);
        if (idx !== -1) {
            this.uploadJobs[idx] = {...this.uploadJobs[idx], ...updates};
        }
    }

    private _persistUploadJobs(): void {
        const serializable = this.uploadJobs.map(({file, ...rest}) => rest);
        sessionStorage.setItem(FileUploadStore.UPLOAD_JOBS_KEY, JSON.stringify(serializable));
    }
}
